import type { CTAData } from "../contexts/chatbot-config/chatbot-config-context";
import { isDirectNavigationLink, trackLinkClick } from "./fns";
import { oauth_key, oauth_domain } from "../constants";

export type CtaEntry = {
  key: string;
  sortNumber: number;
  title: string;
  image?: string;
  link?: string;
  rawDownloadPaths: string[];
  isForm: boolean;
  isLink: boolean;
};

export function encodeS3Path(path: string): string {
  if (!path) return "";
  return path.split("/").map((part) => encodeURIComponent(part)).join("/");
}

export function resolveBucketAsset(path: string | undefined | null, baseUrl: string): string {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (!baseUrl) return path;
  return `${baseUrl}/${encodeS3Path(path)}`;
}

export function normalizeSortNumber(value?: number | string): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

// resol_metis /chat の buildCtaCarouselEntries と同一の判定ロジック
export function buildCtaEntries(payload: CTAData | null | undefined, baseUrl: string): CtaEntry[] {
  if (!payload?.cta_data || payload.cta_data.length === 0) return [];
  return [...payload.cta_data]
    .sort((a, b) => normalizeSortNumber(a.cta_sort ?? a.data_sort) - normalizeSortNumber(b.cta_sort ?? b.data_sort))
    .slice(0, 3)
    .map((item, index) => {
      const isFormType = item.type === 1 || item.type === "1";
      // cta_type=2(電話)は tel: リンクを download_link に入れて返すため直接リンク扱いにする
      const isExplicitLinkType =
        item.type === 0 || item.type === "0" || item.type === "link" || item.type === 2 || item.type === "2";
      let allRawLinks: string[] = [];
      if (Array.isArray(item.download_link)) {
        allRawLinks = item.download_link.filter((l): l is string => typeof l === "string" && !!l);
      } else if (typeof item.download_link === "string" && item.download_link) {
        allRawLinks = [item.download_link];
      }
      const directLink = [item.url, item.cta_link, item.link].find(
        (v): v is string => typeof v === "string" && !!v && v !== "#" && v !== "Bottom",
      );
      const navigationCandidate = isExplicitLinkType
        ? allRawLinks.find((v) => isDirectNavigationLink(v))
        : undefined;
      const navigationLink = directLink ?? navigationCandidate;
      const rawLink = allRawLinks[0] ?? navigationLink;
      const isDownloadForm = item.dl_type === 1 || item.dl_type === "1";
      const shouldOpenForm = isDownloadForm || rawLink === "Bottom" || isFormType;
      const resolveLink = (l: string) =>
        l && l !== "#" && l !== "Bottom" && !isDirectNavigationLink(l) ? resolveBucketAsset(l, baseUrl) : l;
      const normalizedDirectLink = navigationLink ? resolveLink(navigationLink) : undefined;
      const hasDownloadLink = allRawLinks.length > 0;
      const isLink = !shouldOpenForm && !!normalizedDirectLink && (!hasDownloadLink || isExplicitLinkType || !!directLink);
      const rawDownloadPaths = shouldOpenForm || isLink ? [] : allRawLinks.filter((l) => l !== "#" && l !== "Bottom");
      const normalizedLink = isLink ? normalizedDirectLink : rawDownloadPaths.map(resolveLink)[0];
      return {
        key: `${item.title || "cta"}-${index}`,
        sortNumber: normalizeSortNumber(item.cta_sort ?? item.data_sort),
        title: item.title || "資料",
        image: item.image ? resolveBucketAsset(item.image, baseUrl) : undefined,
        link: shouldOpenForm ? undefined : normalizedLink,
        rawDownloadPaths,
        isForm: shouldOpenForm,
        isLink,
      };
    });
}

function triggerAnchorDownload(url: string, filename: string, newTab: boolean) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  if (newTab) {
    a.target = "_blank";
    a.rel = "noopener noreferrer";
  }
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function downloadWithBlob(url: string, filename: string) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Fetch failed");
    const blobUrl = window.URL.createObjectURL(await response.blob());
    triggerAnchorDownload(blobUrl, filename, false);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
  } catch {
    triggerAnchorDownload(url, filename, true);
  }
}

// ウィジェットはサードパーティドメインで動作し /chat のCookie付きプロキシを使えないため、
// 署名付きURL API経由で1ファイルずつダウンロードする
export async function downloadCtaFiles(files: string[], baseUrl: string) {
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const filename = file.split("/").pop() || "download";
    const isPdf = filename.toLowerCase().endsWith(".pdf");
    const download = (url: string) => (isPdf ? downloadWithBlob(url, filename) : triggerAnchorDownload(url, filename, true));
    const fallbackUrl = file.startsWith("http") ? file : `${baseUrl}/${encodeS3Path(file)}`;

    if (i > 0) await new Promise((resolve) => setTimeout(resolve, 300));

    try {
      const response = await fetch(
        `/api/get-download-url/?file_path=${encodeURIComponent(file)}&oauth_key=${encodeURIComponent(oauth_key || "")}&oauth_domain=${encodeURIComponent(oauth_domain || "")}`,
      );
      if (!response.ok) throw new Error("Failed to get download URL");
      const data = await response.json();
      if (data.success && data.download_url) {
        await download(data.download_url);
        trackLinkClick(data.download_url).catch(() => {});
      } else {
        await download(fallbackUrl);
      }
    } catch {
      await download(fallbackUrl);
    }
  }
}

export function extractYoutubeId(value?: string | null): string | null {
  if (!value) return null;
  if (/^[A-Za-z0-9_-]{6,}$/.test(value)) return value;
  const match = value.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return match?.[1] ?? null;
}
